import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function ContactPage() {
  const [params] = useSearchParams();
  const topic = params.get('topic') || '';
  const [form, setForm] = useState({ name: '', email: '', company: '', topic, message: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError('Please fill out name, email, and message.');
      return;
    }
    setError('');
    setSent(true);
  };

  return (
    <div>
      {/* Hero */}
      <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '96px 0 64px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(249,115,22,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container-narrow" style={{ position: 'relative' }}>
          <span className="chip" style={{ marginBottom: 20 }}>💬 We're listening</span>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 58px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 18 }}>
            Let's <span className="gradient-text">talk.</span>
          </h1>
          <p style={{ fontSize: 18, opacity: 0.86, maxWidth: 580, margin: '0 auto', lineHeight: 1.6 }}>
            Questions, partnerships, bug reports, compliments — all welcome. A real human replies to every message, usually within a business day.
          </p>
        </div>
      </section>

      {/* Contact grid */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 56, alignItems: 'start' }} className="contact-grid">
            {/* Form */}
            <div style={{ background: '#fff', border: '1px solid #E4E7F0', borderRadius: 20, padding: 40, boxShadow: '0 4px 18px rgba(15,18,37,0.04)' }}>
              {sent ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: 64, marginBottom: 16 }}>✓</div>
                  <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 10, letterSpacing: '-0.02em' }}>Message sent!</h3>
                  <p style={{ fontSize: 15, color: '#4A5568', lineHeight: 1.65 }}>
                    Thanks for reaching out. We'll get back to you at <strong>{form.email}</strong> within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8, letterSpacing: '-0.02em' }}>Send us a message</h2>
                  <p style={{ fontSize: 14, color: '#8B94A8', marginBottom: 28 }}>Fill out the form and we'll get back to you shortly.</p>

                  {error && <div className="alert alert-danger">{error}</div>}

                  {/* auto-fit: the pairs stack below ~414px. Forced side by
                      side at 320px each field was ~137px wide, and the
                      two-line "Company (optional)" label pushed its input
                      out of line with Topic's. */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 14 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Name *</label>
                      <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Email *</label>
                      <input type="email" className="form-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 14 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Company <span style={{ color: '#8B94A8', fontWeight: 400 }}>(optional)</span></label>
                      <input className="form-input" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Acme Inc." />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Topic</label>
                      <select className="form-input form-select" value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })}>
                        <option value="">Select a topic</option>
                        <option value="general">General inquiry</option>
                        <option value="partnership">Partnership / Brand</option>
                        <option value="enterprise">Enterprise / White-label</option>
                        <option value="press">Press</option>
                        <option value="support">Shopper support</option>
                        <option value="bug">Bug report</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Message *</label>
                    <textarea className="form-input" rows={6} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Tell us how we can help…" />
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg btn-full">Send message →</button>
                  <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 14, textAlign: 'center' }}>By submitting, you agree to our <a href="/privacy">privacy policy</a>.</p>
                </form>
              )}
            </div>

            {/* Side info */}
            <div>
              <ContactBlock icon="📧" title="Email us directly" lines={[{ label: 'General', value: 'hello@perkvora.com', href: 'mailto:hello@perkvora.com' }, { label: 'Partnerships', value: 'partners@perkvora.com', href: 'mailto:partners@perkvora.com' }, { label: 'Press', value: 'press@perkvora.com', href: 'mailto:press@perkvora.com' }, { label: 'Privacy', value: 'privacy@perkvora.com', href: 'mailto:privacy@perkvora.com' }]} />
              <ContactBlock icon="⏱️" title="Response time" lines={[{ value: 'Within 1 business day for most inquiries. Priority support for Pro & Enterprise customers is 4 hours.' }]} />
              <ContactBlock icon="🌎" title="Based in the US" lines={[{ value: 'We operate globally, with headquarters in the United States and partners across the UK, EU, and APAC.' }]} />
              <ContactBlock icon="💬" title="Prefer social?" lines={[{ label: 'Twitter', value: '@perkvora', href: 'https://twitter.com/perkvora' }, { label: 'LinkedIn', value: 'PerkVora', href: 'https://linkedin.com/company/perkvora' }]} />
            </div>
          </div>
          <style>{`@media(max-width:880px){.contact-grid{grid-template-columns:1fr!important;gap:32px!important}}`}</style>
        </div>
      </section>
    </div>
  );
}

function ContactBlock({ icon, title, lines }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{icon}</div>
        <h3 style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</h3>
      </div>
      <div style={{ paddingLeft: 46, fontSize: 14, color: '#4A5568', lineHeight: 1.7 }}>
        {lines.map((l, i) => (
          <div key={i} style={{ marginBottom: 4 }}>
            {l.label && <span style={{ color: '#8B94A8', fontWeight: 500 }}>{l.label}: </span>}
            {l.href ? <a href={l.href} style={{ color: '#6B2FD9', fontWeight: 500 }}>{l.value}</a> : l.value}
          </div>
        ))}
      </div>
    </div>
  );
}
